// src/components/dashboard components/forms/CareerHeroForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Upload, X, Save } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CareerHeroForm = () => {
  const [formData, setFormData] = useState({
    career_hero_id: 0,
    title: "",
    subtitle: "",
    description: "",
    image: "",
  });

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Fetch existing data on mount
  useEffect(() => {
    fetchCareerHero();
  }, []);

  const fetchCareerHero = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/career/admin/hero`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.length > 0) {
        const data = response.data[0];
        setFormData({
          career_hero_id: data.career_hero_id || 0,
          title: data.title || "",
          subtitle: data.subtitle || "",
          description: data.description || "",
          image: data.image || "",
        });
      }
    } catch (error) {
      console.error("Error fetching career hero:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

const handleImageUpload = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const formDataUpload = new FormData();
  formDataUpload.append("media", file);

  axios
    .post(`${API_BASE_URL}/admin/upload-media`, formDataUpload, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => {
      console.log("upload response", res.data);

      const uploadedUrl = res.data.data.files?.[0]?.url; // get S3 URL
      console.log("uploadedUrl", uploadedUrl);
      
      if (uploadedUrl) {
        setFormData((prev) => ({
          ...prev,
          image: uploadedUrl,
        }));
      }
    })
    .catch((err) => {
      console.error("upload-media error", err);
      alert("Failed to upload image. Please try again.");
    });
};

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: "",
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert("Title is required!");
      return;
    }

    setSaveLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE_URL}/career/admin/hero/save`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Career hero saved successfully!");
      fetchCareerHero(); // Refresh data
    } catch (error) {
      console.error("Error saving career hero:", error);
      alert("Failed to save career hero. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Career Hero Section</h2>

      {/* Title */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Enter hero title"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
        />
      </div>

      {/* Subtitle */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Subtitle
        </label>
        <input
          type="text"
          name="subtitle"
          value={formData.subtitle}
          onChange={handleChange}
          placeholder="Enter hero subtitle"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Description
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter hero description"
          rows="4"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
        />
      </div>

      {/* Image Upload */}
      <div>
  <label className="block text-sm font-semibold text-gray-700 mb-2">
    Hero Image
  </label>
  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition">
    {formData.image ? (
      <div className="relative inline-block">
        <img
          src={formData.image}
          alt="Hero"
          className="max-h-64 rounded-lg shadow-md"
        />
        <button
          onClick={removeImage}
          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
        >
          <X size={16} />
        </button>
      </div>
    ) : (
      <label className="cursor-pointer">
        <Upload className="mx-auto mb-2 text-gray-400" size={48} />
        <p className="text-sm text-gray-700">Click to upload image</p>
        <span className="text-xs text-gray-600">
          Recommended image size: <strong>1600 × 900 px</strong> (16:9), JPG/PNG, under 400 KB
        </span>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
      </label>
    )}
  </div>
</div>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <Save size={20} />
          {saveLoading ? "Saving..." : "Save Hero Section"}
        </button>
      </div>
    </div>
  );
};

export default CareerHeroForm;